import { Category } from '../types/Category';
import { Content } from '../types/Content';

export function updateCategoryStructure(
  categories: Category[],
  draggedCategory: Category,
  targetCategory: Category,
  position: 'before' | 'after' | 'inside'
): Category[] {
  const removeCategoryId = (ids: string[], idToRemove: string): string[] => {
    return ids.filter(id => id !== idToRemove);
  };

  const removeCategory = (categories: Category[], categoryToRemove: Category): Category[] => {
    return categories.filter(cat => {
      if (cat.id === categoryToRemove.id) {
        return false;
      }
      if (cat.children) {
        cat.children = removeCategory(cat.children, categoryToRemove);
      }
      return true;
    });
  };

  const insertCategory = (cats: Category[], categoryToInsert: Category, target: Category, pos: 'before' | 'after' | 'inside'): Category[] => {
    return cats.map(cat => {
      if (cat.id === target.id) {
        if (pos === 'inside') {
          return {
            ...cat,
            children: [...(cat.children || []), categoryToInsert]
          };
        }
      }
      if (cat.children) {
        cat.children = insertCategory(cat.children, categoryToInsert, target, pos);
      }
      return cat;
    });
  };

  // First remove the dragged category from its current position
  const categoriesWithoutDragged = removeCategory([...categories], draggedCategory);

  // Then insert it at the new position
  return insertCategory(categoriesWithoutDragged, draggedCategory, targetCategory, position);
}

/**
 * Checks if content with the same subcase and text already exists in a category
 * @param category The category to check
 * @param subcase The subcase to check
 * @param text The text content to check
 * @param excludeContentId Optional content ID to exclude from the check (for editing existing content)
 * @returns true if duplicate exists, false otherwise
 */
export const hasDuplicateContent = (
  category: Category, 
  subcase: string, 
  text: string, 
  excludeContentId?: string
): boolean => {
  if (!category.contents) return false;
  
  return category.contents.some(content => {
    // Skip the content being edited if excludeContentId is provided
    if (excludeContentId && content.id === excludeContentId) {
      return false;
    }
    
    // Check if subcase and text match exactly
    return content.subcase === subcase && content.text === text;
  });
};

/**
 * Gets a user-friendly message for duplicate content
 * @param subcase The subcase that has a duplicate
 * @returns A formatted warning message
 */
export const getDuplicateContentMessage = (subcase: string): string => {
  return `Content with subcase "${subcase}" and the same text already exists in this category. Please modify the content or choose a different subcase.`;
};

export const getContentIndex = (contentId: string): string => {
  const lastIndex = contentId.lastIndexOf(":");
  if (lastIndex === -1) {
    // Delimiter not found, return the original string or handle as needed
    return contentId;
  }
  return contentId.substring(lastIndex + 1);
};